/* eslint-disable max-statements */
import {
  adjustConditionsForDateMode,
  allowedLogicConditionTypes,
  DateTimeType,
  ControlType,
} from '../../../../../shared/form-lib';

export const SUBMIT_BUTTON_ID = 'SUBMIT_BUTTON'; // since identifiers are strings

export const icons = {
  delete: 'https://storage.googleapis.com/fplsblog/Delete.svg',
  edit: 'https://storage.googleapis.com/fplsblog/edit-icon.svg',
};

export const onDateCalendarReady = (instance, disableYearEffect) => {
  const buttons = document.createElement('div');
  buttons.className = 'button-container';

  // Done button
  const done = document.createElement('button');
  done.innerText = 'Done';
  done.className = 'done';
  done.onclick = () => instance.close();

  // Clear button
  const clear = document.createElement('button');
  clear.innerText = 'Clear';
  clear.className = 'clear';
  clear.onclick = () => {
    instance.clear();
    instance.close();
  };

  buttons.append(clear, done);

  if (instance?.calendarContainer) {
    if (disableYearEffect) {
      instance.calendarContainer.querySelector('.numInputWrapper').style.display = 'none';
      instance.calendarContainer.querySelector('.flatpickr-weekdays').style.display = 'none';
    }

    instance.calendarContainer.appendChild(buttons);
  }
};

export const ruleOptions = [
  { text: 'ALL', value: 'all', key: 'all' },
  { text: 'ANY', value: 'any', key: 'any' },
];

export const optionLabels = {
  any: 'ANY',
  all: 'ALL',
};

export const actionTypes = [
  { text: 'Show', value: 'show', key: 'show' },
  { text: 'Hide', value: 'hide', key: 'hide' },
];

export const actionLabels = {
  show: 'Show',
  hide: 'Hide',
};

export const conditionHasParam = {
  equal: true,
  not_equal: true,
  contain: true,
  not_contain: true,
  greater_than: true,
  greater_than_equal: true,
  less_than: true,
  less_than_equal: true,
  between: true,
};

export const conditionLabels = (ctrlType) => {
  const isForDateTimeType = ctrlType === ControlType.DATETIME;
  return {
    empty: 'is empty',
    not_empty: 'is not empty',
    equal: 'equals',
    not_equal: "doesn't equal",
    contain: 'contains',
    not_contain: 'does not contain',
    greater_than: isForDateTimeType ? 'is later than' : 'is greater than',
    greater_than_equal: isForDateTimeType ? 'is later than or equal to' : 'is greater than or equal to',
    less_than: isForDateTimeType ? 'is earlier than' : 'is less than',
    less_than_equal: isForDateTimeType ? 'is earlier than or equal to' : 'is less than or equal to',
    between: 'is between',
  };
};

export const setupRuleAction = (idx, rule) => {
  if (idx === undefined && rule === undefined) {
    return {
      option: 'any',
      actions: [],
      triggers: [],
      ruleIndex: null,
    };
  }

  return {
    ruleIndex: idx,
    option: rule.option,
    actions: rule.actions,
    triggers: rule.triggers,
  };
};

export const renderTriggerParam = (trigger) => {
  if (trigger.type === 'between') {
    const [first, second] = trigger.param ? trigger.param.split(',') : [];
    return `${first || '___'} and ${second || '___'}`;
  }
  return trigger.label || trigger.param;
};

const createStyledLabel = (label) => {
  return `<span style='font-weight: bold'>${label}</span>`;
};

export const isNewRuleCorrect = (newRule, fieldMap, allRules) => {
  const newError = {
    errors: [],
    errorHeader: '',
  };

  const addError = (error, errorHeader = 'Incorrect rule') => {
    newError.errors.push(error);
    newError.errorHeader = errorHeader;
  };

  if ((newRule.actions.length < 1 && newRule.triggers < 1) || newRule.actions.length < 1 || newRule.triggers < 1) {
    const anAction = createStyledLabel('1 action');
    const aCondition = createStyledLabel('1 condition');
    const error =
      newRule.actions.length < 1 && newRule.triggers < 1
        ? `You cannot submit an empty rule, a complete rule should have atleast ${aCondition} and ${anAction}`
        : `A complete rule should have atleast ${aCondition} and ${anAction}`;
    addError(error);
  }

  const triggerFields = {};
  newRule.triggers.forEach((trigger) => {
    const { ctrl, label } = fieldMap[trigger.id];
    const fieldLabel = createStyledLabel(label);
    const conditionType = createStyledLabel(conditionLabels(ctrl.type)[trigger.type]);

    if (!triggerFields[trigger.id]) {
      triggerFields[trigger.id] = true;
    }

    if (conditionHasParam[trigger.type]) {
      if (!trigger.param && trigger.type !== 'between') {
        const error = `Parameter is required for ${conditionType} condition on ${fieldLabel}`;
        addError(error);
      }

      if (trigger.type === 'between') {
        const paramArr = trigger.param.split(',') || [];
        const hasMissingValue = paramArr.some((item) => !item?.trim());

        if (paramArr.length <= 1 || hasMissingValue) {
          const error = `Two parameters are required for ${conditionType} condition on ${fieldLabel}`;
          addError(error);
        }

        if (!hasMissingValue && paramArr[0] === paramArr[1]) {
          const error = `The two parameters for ${conditionType} condition type on ${fieldLabel} cannot be the same`;
          addError(error);
        }
      }
    }

    // Check that field is allowed with specified condition type
    let allowedConditionType = allowedLogicConditionTypes()[ctrl.type];

    if (ctrl.type === ControlType.DATETIME) {
      const mode =
        ctrl.props.type === DateTimeType.DATE || ctrl.props.type === DateTimeType.DATETIME ? ctrl.props.mode : 'single';
      const disableYearEffect = ctrl.props.type === DateTimeType.DATE && ctrl.props?.disable_year;
      allowedConditionType = allowedConditionType.filter(
        (cond) => !adjustConditionsForDateMode(mode, disableYearEffect).includes(cond)
      );
    }

    if (allowedConditionType === undefined) {
      const error = `${fieldLabel} is not supported in condition"`;
      addError(error);
    } else if (!allowedConditionType.includes(trigger.type)) {
      const error = `${fieldLabel} doesn't support ${conditionType} condition type`;
      addError(error);
    }
  });

  // check that triggered field actions are consistent
  const actionFields = {};
  allRules.forEach((rule, index) => {
    if (index !== newRule.ruleIndex) {
      rule.actions.forEach((action) => {
        actionFields[`${action.action}:${action.id}`] = true;
      });
    }
  });

  const ruleActionFields = {};
  newRule.actions.forEach((action) => {
    const { label } = fieldMap[action.id];
    const fieldLabel = createStyledLabel(label);
    if (action.id in ruleActionFields) {
      const error = `${fieldLabel} cannot be used more than once as an action`;
      addError(error);
    }
    ruleActionFields[action.id] = true;
    if (action.id in triggerFields) {
      const error = `${fieldLabel} cannot be used in triggers and actions`;
      addError(error);
    }
    const antiAction = action.action === 'show' ? 'hide' : 'show';
    const conflictingAction = `${antiAction}:${action.id}`;
    if (conflictingAction in actionFields) {
      const error = `${actionLabels[antiAction]} action is used for ${fieldLabel} in another rule`;
      const errorHeader = 'Conflicting Rules';
      addError(error, errorHeader);
    }
  });

  return newError;
};
